// frontend/src/pages/Usuarios/GerenciarUsuarios.jsx

import React, { useState, useEffect, useRef } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import FormLabel from "../../../components/FormLabel";
import usePermission from "../../../hooks/usePermission";
import GerenciarClientesUsuario from "../../../components/usuarios/GerenciarClientesUsuario";
import config from "../../../config";

const GerenciarUsuarios = () => {
    const navigate = useNavigate();

    // Lista de usuários
    const [userList, setUserList] = useState([]);

    // Lista de roles
    const [roleList, setRoleList] = useState([]);

    // Modal de gerenciar clientes
    const [showClientesModal, setShowClientesModal] = useState(false);
    const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

    // Formulário
    const [formData, setFormData] = useState({
        id: null,
        nome: "",
        email: "",
        senha: "",
        roleId: "",
    });

    // Controle do formulário
    const [isEditMode, setIsEditMode] = useState(false);
    const podeAtualizar = usePermission("ATUALIZAR_USUARIO");
    const podeDeletar = usePermission("DELETAR_USUARIO");

    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmBlockDialog, setShowConfirmBlocDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // Estados de erro
    const [errors, setErrors] = useState({});

    // Modal de assinatura
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureUser, setSignatureUser] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState("");
    const [signatureLoading, setSignatureLoading] = useState(false);
    const signatureInputRef = useRef(null);

    // Carregar usuários e roles
    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get("/users");
            setUserList(response.data);
        } catch (error) {
            console.error("Erro ao carregar usuários:", error);
            alert("Erro ao carregar usuários. Tente novamente.");
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await api.get("/roles");
            setRoleList(response.data);
        } catch (error) {
            console.error("Erro ao carregar roles:", error);
            alert("Erro ao carregar roles. Tente novamente.");
        }
    };

    // Manipulador de mudança nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Validação do formulário
    const validateForm = () => {
        const newErrors = {};

        if (!formData.nome.trim()) {
            newErrors.nome = "Nome é obrigatório";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email é obrigatório";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email inválido";
        }

        if (!isEditMode) { // Senha é obrigatória apenas no cadastro
            if (!formData.senha) {
                newErrors.senha = "Senha é obrigatória";
            } else if (!isPasswordStrong(formData.senha)) {
                newErrors.senha = "A senha deve ter pelo menos 8 caracteres, incluindo letras, números e caracteres especiais";
            }
        } else {
            if (formData.senha && !isPasswordStrong(formData.senha)) {
                newErrors.senha = "A senha deve ter pelo menos 8 caracteres, incluindo letras, números e caracteres especiais";
            }
        }

        if (!formData.roleId) {
            newErrors.roleId = "Role é obrigatória";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const isPasswordStrong = (password) => {
        // Pelo menos 8 caracteres, com letras, números e caracteres especiais
        const regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
        return regex.test(password);
    };

    // Submeter o formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowConfirmDialog(true);
        }
    };

    // Confirmar criação/edição
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            if (isEditMode) {
                // Atualizar usuário existente
                const data = {
                    nome: formData.nome,
                    email: formData.email,
                    roleId: parseInt(formData.roleId),
                };
                if (formData.senha) {
                    data.senha = formData.senha;
                }

                await api.put(`/users/${formData.id}`, data);
            } else {
                // Criar novo usuário
                await api.post("/auth/register", {
                    nome: formData.nome,
                    email: formData.email,
                    senha: formData.senha,
                    roleId: parseInt(formData.roleId),
                });
            }

            // Atualizar lista de usuários
            fetchUsers();

            // Mostrar sucesso
            setSuccessMessage(`Usuário ${isEditMode ? "atualizado" : "adicionado"} com sucesso!`);
            setShowSuccessDialog(true);

            // Limpar formulário
            setFormData({
                id: null,
                nome: "",
                email: "",
                senha: "",
                roleId: "",
            });
            setIsEditMode(false);
            setErrors({});
        } catch (error) {
            console.error("Erro ao salvar usuário:", error);
            alert(
                `Erro ao ${isEditMode ? "atualizar" : "cadastrar"} o usuário. Verifique os dados e tente novamente.`
            );
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Editar usuário
    const handleEdit = (user) => {
        setIsEditMode(true);
        setFormData({
            id: user.id,
            nome: user.nome,
            email: user.email,
            senha: "", // Senha não é exibida
            roleId: user.role.id.toString(),
        });
        setErrors({});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Gerenciar clientes do usuário
    const handleGerenciarClientes = (user) => {
        setUsuarioSelecionado(user);
        setShowClientesModal(true);
    };

    const handleCloseClientesModal = () => {
        setShowClientesModal(false);
        setUsuarioSelecionado(null);
    };

    const handleSaveClientes = () => {
        // Recarregar lista de usuários se necessário
        fetchUsers();
    };

    // Deletar usuário
    const handleDelete = (user) => {
        setUserToDelete(user);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        setShowConfirmDelete(false);
        try {
            await api.delete(`/users/${userToDelete.id}`);
            // Atualizar lista de usuários
            fetchUsers();
            setUserToDelete(null);
            setSuccessMessage("Usuário deletado com sucesso!");
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao deletar usuário:", error);
            alert("Erro ao deletar o usuário. Tente novamente.");
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDelete(false);
        setUserToDelete(null);
    };

    // Cancelar edição
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setFormData({
            id: null,
            nome: "",
            email: "",
            senha: "",
            roleId: "",
        });
        setErrors({});
    };

    // ========== FUNÇÕES DE ASSINATURA ==========

    // Abrir modal de assinatura
    const handleOpenSignatureModal = (user) => {
        setSignatureUser(user);
        setSignaturePreview(user.signatureUrl ? config.urlArquivo(user.signatureUrl) : "");
        setSignatureFile(null);
        setShowSignatureModal(true);
    };

    // Fechar modal de assinatura
    const handleCloseSignatureModal = () => {
        setShowSignatureModal(false);
        setSignatureUser(null);
        setSignatureFile(null);
        setSignaturePreview("");
    };

    // Selecionar arquivo de assinatura
    const handleSignatureFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSignatureFile(file);
            setSignaturePreview(URL.createObjectURL(file));
        }
    };

    // Fazer upload da assinatura
    const handleUploadSignature = async () => {
        if (!signatureFile || !signatureUser) return;

        setSignatureLoading(true);
        try {
            const formData = new FormData();
            formData.append("assinatura", signatureFile);

            await api.put(`/users/${signatureUser.id}/signature`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            fetchUsers();
            setSuccessMessage("Assinatura atualizada com sucesso!");
            setShowSuccessDialog(true);
            handleCloseSignatureModal();
        } catch (error) {
            console.error("Erro ao fazer upload da assinatura:", error);
            alert("Erro ao enviar a assinatura. Tente novamente.");
        } finally {
            setSignatureLoading(false);
        }
    };

    // Remover assinatura
    const handleRemoveSignature = async () => {
        if (!signatureUser) return;

        setSignatureLoading(true);
        try {
            await api.delete(`/users/${signatureUser.id}/signature`);
            fetchUsers();
            setSignaturePreview("");
            setSignatureFile(null);
            setSuccessMessage("Assinatura removida com sucesso!");
            setShowSuccessDialog(true);
            handleCloseSignatureModal();
        } catch (error) {
            console.error("Erro ao remover assinatura:", error);
            alert("Erro ao remover a assinatura. Tente novamente.");
        } finally {
            setSignatureLoading(false);
        }
    };

    // Alternar bloqueio/desbloqueio
    const handleBlockToggle = async (user) => {
        if (user.isActive) {
            await confirmBlock(user.id);

        } else {
            // Desbloquear usuário
            await confirmUnblock(user.id);
        }
    };

    // Confirmar bloqueio
    const confirmBlock = async (userId) => {
        setLoading(true);
        try {
            await api.put(`/users/block/${userId}`);
            // Atualizar lista de usuários
            fetchUsers();
        } catch (error) {
            console.error('Erro ao bloquear usuário:', error);
            alert('Erro ao bloquear o usuário. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Confirmar desbloqueio
    const confirmUnblock = async (userId) => {
        setLoading(true);
        try {
            await api.put(`/users/unblock/${userId}`);
            // Atualizar lista de usuários
            fetchUsers();

        } catch (error) {
            console.error('Erro ao desbloquear usuário:', error);
            alert('Erro ao desbloquear o usuário. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        Gerenciar Usuários
                    </h1>
                </div>

                {/* Formulário de Criação/Edição */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 bg-white p-6 rounded-md border border-slate-200"
                >
                    <div className="text-stone-600 text-base font-medium">
                        {isEditMode ? "Editar Usuário" : "Cadastrar Novo Usuário"}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel isRequired={true} htmlFor={"nome"} className="text-sm text-slate-700 font-medium">
                                Nome
                            </FormLabel>
                            <input
                                type="text"
                                name="nome"
                                value={formData.nome}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.nome ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite o nome do usuário"
                                required
                            />
                            {errors.nome && (
                                <p className="text-red-600 text-sm">{errors.nome}</p>
                            )}
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel isRequired={true} htmlFor={"email"} className="text-sm text-slate-700 font-medium">
                                E-mail
                            </FormLabel>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.email ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite o email do usuário"
                                required
                            />
                            {errors.email && (
                                <p className="text-red-600 text-sm">{errors.email}</p>
                            )}
                        </div>

                        {/* Senha */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Senha
                                {!isEditMode && <span className="text-red-600"> *</span>}
                            </label>
                            <input
                                type="password"
                                name="senha"
                                value={formData.senha}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.senha ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder={
                                    isEditMode
                                        ? "Digite para atualizar a senha (opcional)"
                                        : "Digite a senha do usuário"
                                }
                                required={!isEditMode}
                            />
                            {errors.senha && (
                                <p className="text-red-600 text-sm">{errors.senha}</p>
                            )}
                        </div>

                        {/* Role */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel isRequired={true} htmlFor={"roleId"} className="text-sm text-slate-700 font-medium">
                                Perfil
                            </FormLabel>
                            <select
                                name="roleId"
                                value={formData.roleId}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.roleId ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                required
                            >
                                <option value="">Selecionar</option>
                                {roleList.map((role) => {
                                    const ehAdmin = role.nome === "ADMIN";
                                    const adminJaAtribuido = ehAdmin && userList.some(
                                        (u) => u.role?.nome === "ADMIN" && u.id !== formData.id
                                    );
                                    return (
                                        <option
                                            key={role.id}
                                            value={role.id}
                                            disabled={adminJaAtribuido}
                                        >
                                            {role.nome}
                                            {adminJaAtribuido ? " (já atribuído a outro usuário)" : ""}
                                        </option>
                                    );
                                })}
                            </select>
                            {formData.roleId && roleList.find(r => r.id === parseInt(formData.roleId))?.nome === "ADMIN" && (
                                <p className="text-amber-700 text-xs">
                                    🔒 ADMIN é exclusivo: somente um usuário pode tê-lo, e ele recebe todas as permissões automaticamente.
                                </p>
                            )}
                            {errors.roleId && (
                                <p className="text-red-600 text-sm">{errors.roleId}</p>
                            )}
                        </div>
                    </div>

                    {/* Botões do Formulário */}
                    <div className="flex items-center gap-4">
                        {isEditMode && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            >
                                Cancelar
                            </button>
                        )}
                        {usePermission('CRIAR_USUARIO') && (
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                            >
                                {isEditMode ? "Salvar Alterações" : "Adicionar Usuário"}
                            </button>
                        )}
                    </div>
                </form>

                {/* Lista de Usuários */}
                <div className="bg-white p-6 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium mb-4">
                        Lista de Usuários
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">ID</th>
                                    <th className="px-4 py-2 border border-slate-200">Nome</th>
                                    <th className="px-4 py-2 border border-slate-200">Email</th>
                                    <th className="px-4 py-2 border border-slate-200">Perfil</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userList.length > 0 ? (
                                    userList.map((user) => (
                                        <tr key={user.id} className="border-b">
                                            <td className="px-4 py-2 border border-slate-200">
                                                {user.id}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {user.nome}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {user.email}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {user.role.nome}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(user)}
                                                        className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                    >
                                                        Editar
                                                    </button>
                                                )}

                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleGerenciarClientes(user)}
                                                        className="px-2 py-1 bg-blue-600 text-white rounded-md text-sm mr-2"
                                                        title="Gerenciar clientes que o usuário pode acessar"
                                                    >
                                                        Clientes
                                                    </button>
                                                )}

                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenSignatureModal(user)}
                                                        className="px-2 py-1 bg-indigo-600 text-white rounded-md text-sm mr-2"
                                                        title="Gerenciar assinatura do usuário"
                                                    >
                                                        Assinatura
                                                    </button>
                                                )}

                                                {user.isActive && podeAtualizar ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBlockToggle(user)}
                                                        className="px-2 py-1 bg-orange-600 text-white rounded-md text-sm mr-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Bloqueando...' : 'Bloquear'}
                                                    </button>
                                                ) : (
                                                    podeAtualizar && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleBlockToggle(user)}
                                                            className="px-2 py-1 bg-green-600 text-white rounded-md text-sm mr-2"
                                                            disabled={loading}
                                                        >
                                                            {loading ? 'Desbloqueando...' : 'Desbloquear'}
                                                        </button>
                                                    )
                                                )}
                                                {/* Botão Deletar */}
                                                {podeDeletar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(user)}
                                                        className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                                                    >
                                                        Deletar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-4 py-2 text-center text-slate-700"
                                        >
                                            Nenhum usuário cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modais de Confirmação */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message={`Deseja ${isEditMode ? "salvar as alterações" : "adicionar este usuário"}?`}
            />

            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={`Deseja deletar o usuário "${userToDelete?.nome}"?`}
                textButtonConfirm="Sim, Deletar"
            />

            {/* Modal de Sucesso */}
            <SuccessModal
                isVisible={showSuccessDialog}
                message={successMessage}
                onClose={() => setShowSuccessDialog(false)}
            />

            {/* Modal de Gerenciar Clientes */}
            {showClientesModal && usuarioSelecionado && (
                <GerenciarClientesUsuario
                    userId={usuarioSelecionado.id}
                    onClose={handleCloseClientesModal}
                    onSave={handleSaveClientes}
                />
            )}

            {/* Modal de Assinatura */}
            {showSignatureModal && signatureUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                        <h2 className="text-lg font-semibold text-stone-800 mb-4">
                            Assinatura - {signatureUser.nome}
                        </h2>

                        {/* Preview da assinatura */}
                        <div className="flex flex-col items-center gap-4 mb-4">
                            {signaturePreview ? (
                                <div className="border border-slate-200 rounded-md p-3 bg-white">
                                    <img
                                        src={signaturePreview}
                                        alt="Assinatura"
                                        className="max-w-full max-h-40 object-contain"
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-32 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50">
                                    <span className="text-slate-400 text-sm">Nenhuma assinatura cadastrada</span>
                                </div>
                            )}

                            {/* Input de arquivo */}
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={handleSignatureFileChange}
                                ref={signatureInputRef}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => signatureInputRef.current?.click()}
                                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                            >
                                {signaturePreview ? "Trocar Assinatura" : "Selecionar Assinatura"}
                            </button>
                            <p className="text-xs text-slate-500">
                                Formatos aceitos: PNG, JPG, WEBP. Recomendado: fundo transparente (PNG).
                            </p>
                        </div>

                        {/* Botões de ação */}
                        <div className="flex items-center justify-between gap-4 pt-4 border-t border-slate-200">
                            <div>
                                {signatureUser.signatureUrl && !signatureFile && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveSignature}
                                        className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                                        disabled={signatureLoading}
                                    >
                                        {signatureLoading ? "Removendo..." : "Remover"}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleCloseSignatureModal}
                                    className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md hover:bg-stone-500"
                                >
                                    Cancelar
                                </button>
                                {signatureFile && (
                                    <button
                                        type="button"
                                        onClick={handleUploadSignature}
                                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                                        disabled={signatureLoading}
                                    >
                                        {signatureLoading ? "Salvando..." : "Salvar Assinatura"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Template>
    );
};

export default GerenciarUsuarios;
